import { useEffect, useRef, useState } from "react";
import { Capacitor } from "@capacitor/core";

interface SwipeGestureOptions {
  /**
   * Minimum distance in pixels to trigger a swipe
   * @default 50
   */
  threshold?: number;

  /**
   * Maximum vertical deviation allowed for horizontal swipe
   * @default 100
   */
  verticalThreshold?: number;

  /**
   * Edge zone width in pixels where swipe can start (from left edge)
   * @default 20
   */
  edgeZone?: number;

  /**
   * Whether to enable the swipe gesture
   * @default true
   */
  enabled?: boolean;

  /**
   * Callback when swipe back is detected
   */
  onSwipeBack?: () => void;

  /**
   * Callback when swipe forward is detected
   */
  onSwipeForward?: () => void;
}

interface SwipeState {
  isSwiping: boolean;
  swipeProgress: number; // 0 to 1
  direction: "left" | "right" | null;
}

export function useSwipeGesture(options: SwipeGestureOptions = {}) {
  const {
    threshold = 50,
    verticalThreshold = 100,
    edgeZone = 20,
    enabled = true,
    onSwipeBack,
    onSwipeForward,
  } = options;

  const [swipeState, setSwipeState] = useState<SwipeState>({
    isSwiping: false,
    swipeProgress: 0,
    direction: null,
  });

  const touchStartRef = useRef<{ x: number; y: number; time: number } | null>(null);
  const touchCurrentRef = useRef<{ x: number; y: number } | null>(null);
  const isSwipingRef = useRef(false);

  useEffect(() => {
    // Only enable on iOS native
    if (!enabled || !Capacitor.isNativePlatform() || Capacitor.getPlatform() !== "ios") {
      return;
    }

    const handleTouchStart = (e: TouchEvent) => {
      const touch = e.touches[0];
      const startX = touch.clientX;
      const startY = touch.clientY;

      // Only start tracking if touch starts near the left edge
      if (startX <= edgeZone) {
        touchStartRef.current = {
          x: startX,
          y: startY,
          time: Date.now(),
        };
        touchCurrentRef.current = { x: startX, y: startY };
      }
    };

    const handleTouchMove = (e: TouchEvent) => {
      if (!touchStartRef.current) return;

      const touch = e.touches[0];
      const currentX = touch.clientX;
      const currentY = touch.clientY;

      touchCurrentRef.current = { x: currentX, y: currentY };

      const deltaX = currentX - touchStartRef.current.x;
      const deltaY = Math.abs(currentY - touchStartRef.current.y);

      // Check if this is a horizontal swipe
      if (Math.abs(deltaX) > 10 && deltaY < verticalThreshold) {
        // Prevent default to avoid conflicts with native gestures
        if (deltaX > 0) {
          e.preventDefault();
        }

        isSwipingRef.current = true;

        const progress = Math.min(Math.abs(deltaX) / (threshold * 3), 1);
        const direction = deltaX > 0 ? "right" : "left";

        setSwipeState({
          isSwiping: true,
          swipeProgress: progress,
          direction,
        });
      }
    };

    const handleTouchEnd = () => {
      if (!touchStartRef.current || !touchCurrentRef.current || !isSwipingRef.current) {
        resetSwipe();
        return;
      }

      const deltaX = touchCurrentRef.current.x - touchStartRef.current.x;
      const deltaY = Math.abs(touchCurrentRef.current.y - touchStartRef.current.y);
      const deltaTime = Date.now() - touchStartRef.current.time;

      // Calculate velocity (pixels per millisecond)
      const velocity = Math.abs(deltaX) / deltaTime;

      // Trigger swipe if threshold is met or velocity is high enough
      const shouldTrigger = Math.abs(deltaX) >= threshold || velocity > 0.5;

      if (shouldTrigger && deltaY < verticalThreshold) {
        if (deltaX > 0 && onSwipeBack) {
          onSwipeBack();
        } else if (deltaX < 0 && onSwipeForward) {
          onSwipeForward();
        }
      }

      resetSwipe();
    };

    const handleTouchCancel = () => {
      resetSwipe();
    };

    const resetSwipe = () => {
      touchStartRef.current = null;
      touchCurrentRef.current = null;
      isSwipingRef.current = false;
      setSwipeState({
        isSwiping: false,
        swipeProgress: 0,
        direction: null,
      });
    };

    // Add event listeners with passive: false to allow preventDefault
    document.addEventListener("touchstart", handleTouchStart, { passive: true });
    document.addEventListener("touchmove", handleTouchMove, { passive: false });
    document.addEventListener("touchend", handleTouchEnd, { passive: true });
    document.addEventListener("touchcancel", handleTouchCancel, { passive: true });

    return () => {
      document.removeEventListener("touchstart", handleTouchStart);
      document.removeEventListener("touchmove", handleTouchMove);
      document.removeEventListener("touchend", handleTouchEnd);
      document.removeEventListener("touchcancel", handleTouchCancel);
    };
  }, [enabled, threshold, verticalThreshold, edgeZone, onSwipeBack, onSwipeForward]);

  return swipeState;
}
